/**
 * The store: Remove Ads and Dropstack Plus with plain prices
 * (docs/MONETIZATION.md § 3). No countdowns, no "was" prices, no pre-selected
 * upsell. Subscriptions say they renew and how to cancel before the buy
 * button. In the prototype every purchase is labelled as a test.
 *
 * Opened only after the parental gate, and every Buy asks for the gate again,
 * so an adult who only opened the store to look never hands the device back
 * with a live Buy button. Plus perks that aren't built yet are marked
 * "coming soon" (PlusFeatures.isAvailable), never listed as included.
 */
import { useEffect, useId, useState } from 'react';
import type { Entitlements, PlusFeatureId, PlusFeatures, Product, ProductId, PurchaseService } from '../contracts.ts';
import { PLUS_FEATURE_IDS } from '../services/plusFeatures.ts';
import { getLocale, useT } from '../i18n/index.ts';
import { Button } from './Button.tsx';
import { Dialog } from './Dialog.tsx';
import { Icon } from './Icon.tsx';
import { Spinner } from './Spinner.tsx';
import './ui.css';

export interface StoreSheetProps {
  purchases: PurchaseService;
  entitlements: Entitlements;
  /** Show "Test purchase — no money is charged" (the mock store). */
  testMode: boolean;
  /** Which Plus perks exist in this build (the rest say "coming soon"). */
  plusFeatures: Pick<PlusFeatures, 'isAvailable'>;
  /** Asked before every purchase (resolves false when the gate was cancelled). */
  requestGate(): Promise<boolean>;
  onClose(): void;
}

type Status = { kind: 'idle' } | { kind: 'buying'; id: ProductId } | { kind: 'done'; key: string };

const TITLE_KEYS: Record<ProductId, string> = {
  remove_ads: 'shell.storeRemoveAds',
  plus_monthly: 'shell.storePlusMonthly',
  plus_yearly: 'shell.storePlusYearly',
};

const PRICE_KEYS: Record<ProductId, string> = {
  remove_ads: 'shell.storePriceOnce',
  plus_monthly: 'shell.storePricePerMonth',
  plus_yearly: 'shell.storePricePerYear',
};

const PERK_KEYS: Record<PlusFeatureId, string> = {
  practicePacks: 'shell.storePerkPractice',
  bonusThemes: 'shell.storePerkThemes',
  parentReport: 'shell.storePerkReport',
};

const RESULT_KEYS = {
  purchased: 'shell.storeThanks',
  cancelled: 'shell.storeCancelled',
  failed: 'shell.storeFailed',
} as const;

export function StoreSheet({ purchases, entitlements, testMode, plusFeatures, requestGate, onClose }: StoreSheetProps) {
  const t = useT();
  const titleId = useId();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  useEffect(() => {
    let live = true;
    purchases.getProducts().then(
      (list) => live && setProducts(list),
      () => live && setProducts([]),
    );
    return () => {
      live = false;
    };
  }, [purchases]);

  const buy = async (id: ProductId) => {
    if (!(await requestGate())) return;
    setStatus({ kind: 'buying', id });
    const outcome = await purchases.purchase(id).catch(() => 'failed' as const);
    setStatus({ kind: 'done', key: RESULT_KEYS[outcome] });
  };

  const owned = (p: Product): boolean => (p.kind === 'one-time' ? entitlements.adsRemoved : entitlements.plus);
  const plusUntil = entitlements.plusExpiresAt ? formatDate(entitlements.plusExpiresAt) : null;
  const ownedLabel = (p: Product): string => {
    if (p.kind === 'subscription') return plusUntil ? t('shell.storeActiveUntil', { date: plusUntil }) : t('shell.storeOwned');
    return entitlements.plus ? t('shell.storeIncludedInPlus') : t('shell.storeOwned');
  };

  return (
    <Dialog onClose={onClose} labelledBy={titleId} variant="sheet" className="ui-store">
      <div className="ui-dialog__head">
        <Icon name="bag" size="1.6em" />
        <h2 id={titleId} className="ui-dialog__title">
          {t('shell.storeTitle')}
        </h2>
      </div>
      {testMode && (
        <p className="ui-store__test">
          <Icon name="info" />
          {t('shell.storeTestNote')}
        </p>
      )}

      <div className="ui-store__plus">
        <h3 className="ui-store__heading">{t('shell.storePlusTitle')}</h3>
        <ul className="ui-store__perks">
          <li>{t('shell.storePerkNoAds')}</li>
          {PLUS_FEATURE_IDS.map((id) =>
            plusFeatures.isAvailable(id) ? (
              <li key={id}>{t(PERK_KEYS[id])}</li>
            ) : (
              <li key={id} className="ui-store__perk--soon">
                {t(PERK_KEYS[id])} <span className="ui-store__soon">{t('shell.storeComingSoon')}</span>
              </li>
            ),
          )}
        </ul>
        <p className="ui-store__fair">{t('shell.storeFair')}</p>
      </div>

      {products === null ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <ul className="ui-store__products">
          {products.map((p) => (
            <li key={p.id} className="ui-store__product">
              <div className="ui-store__product-text">
                <span className="ui-store__product-title">{t(TITLE_KEYS[p.id])}</span>
                <span className="ui-store__price">{t(PRICE_KEYS[p.id], { price: p.priceLabel })}</span>
                {p.kind === 'subscription' && <span className="ui-store__renews">{t('shell.storeRenews')}</span>}
              </div>
              {owned(p) ? (
                <span className="ui-store__owned">
                  <Icon name="check" />
                  {ownedLabel(p)}
                </span>
              ) : (
                <Button
                  variant="primary"
                  busy={status.kind === 'buying' && status.id === p.id}
                  disabled={status.kind === 'buying'}
                  onClick={() => void buy(p.id)}
                >
                  {testMode ? t('shell.storeBuyTest') : t('shell.storeBuy')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="ui-store__status" role="status">
        {status.kind === 'done' ? t(status.key) : ''}
      </p>
      <div className="ui-dialog__actions">
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </Dialog>
  );
}

/** "12 October 2026" / "12 Oktober 2026". */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(getLocale() === 'af' ? 'af-ZA' : 'en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}
